
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { TrendingUp, Users, Mail, MessageSquare } from "lucide-react";

const AnalyticsTab: React.FC = () => {
  // Mock data for analytics - in real implementation, this would come from the database
  const conversionData = [
    { name: 'Step 1', conversions: 100, dropoffs: 20 },
    { name: 'Step 2', conversions: 80, dropoffs: 15 },
    { name: 'Step 3', conversions: 65, dropoffs: 10 },
    { name: 'Step 4', conversions: 55, dropoffs: 8 },
    { name: 'Step 5', conversions: 47, dropoffs: 5 },
  ];

  const engagementData = [
    { date: '2024-01-01', opens: 85, clicks: 42 },
    { date: '2024-01-02', opens: 92, clicks: 48 },
    { date: '2024-01-03', opens: 78, clicks: 35 },
    { date: '2024-01-04', opens: 88, clicks: 44 },
    { date: '2024-01-05', opens: 95, clicks: 52 },
    { date: '2024-01-06', opens: 82, clicks: 38 },
    { date: '2024-01-07', opens: 90, clicks: 46 },
  ];

  const funnelPerformance = [
    { name: 'Welcome Series', value: 65, color: '#3B82F6' },
    { name: 'Profile Completion', value: 78, color: '#10B981' },
    { name: 'Recruiting Reminder', value: 45, color: '#F59E0B' },
    { name: 'Re-engagement', value: 32, color: '#EF4444' },
  ];

  const stats = [
    {
      title: "Active Funnels",
      value: "12",
      change: "+2 this month",
      icon: TrendingUp,
      color: "text-green-600"
    },
    {
      title: "Total Enrollments",
      value: "1,247",
      change: "+18% vs last month",
      icon: Users,
      color: "text-blue-600"
    },
    {
      title: "Email Open Rate",
      value: "68.4%",
      change: "+5.2% vs last month",
      icon: Mail,
      color: "text-purple-600"
    },
    {
      title: "Conversion Rate",
      value: "23.1%",
      change: "+1.8% vs last month",
      icon: MessageSquare,
      color: "text-orange-600"
    }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">{stat.title}</p>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className={`text-xs ${stat.color}`}>{stat.change}</p>
                  </div>
                  <IconComponent className={`h-8 w-8 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Funnel Conversion Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Funnel Conversion Rates</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={conversionData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="conversions" fill="#3B82F6" />
                <Bar dataKey="dropoffs" fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Engagement Trends */}
        <Card>
          <CardHeader>
            <CardTitle>Email Engagement Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={engagementData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="opens" stroke="#3B82F6" strokeWidth={2} />
                <Line type="monotone" dataKey="clicks" stroke="#10B981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Funnel Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Funnel Performance Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={funnelPerformance}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}%`}
                >
                  {funnelPerformance.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            
            <div className="space-y-4">
              <h4 className="font-medium">Top Performing Funnels</h4>
              {funnelPerformance
                .sort((a, b) => b.value - a.value)
                .map((funnel, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: funnel.color }}
                      />
                      <span className="font-medium">{funnel.name}</span>
                    </div>
                    <span className="text-sm font-medium">{funnel.value}%</span>
                  </div>
                ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsTab;
